import { useState, useEffect } from 'react';
import { Toastify, Button, SpinnerLoader, Input, Label, Checkbox, Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter, SingleSelect } from 'lift-ui';
import { Textarea } from '@/components/ui/textarea';
import { AlertCircle } from 'lucide-react';
import { CDType } from '../TaxonomyMerge';
import apiClient from '@lift-admin/utils/apiClient';
import { actionLog } from "@/App";
import {
  buildSummary,
  formatIdWithName,
} from "@lift-admin/utils/logChangeFormatter";

interface CreateDialogProps {
  openMergeDialog: boolean;
  setOpenMergeDialog: (open: boolean) => void;
  selectedCols: number[];
  setSelectedCols: (cols: number[]) => void;
  callTaxonomyMerge: boolean;
  setCallTaxonomyMerge: (call: boolean) => void;
  nonMergedColumns: CDType[];
}

interface AddCDTypeRequest {
  name: string;
  taxonomy_type: string;
  description?: string;
  created_by: number;
  merged_column_ids: number[];
  brand_id: number;
}

const taxonomyTypes = ['campaign', 'ads', 'adsets'];

const TaxonomyMergedCreateDialog: React.FC<CreateDialogProps> = ({
  openMergeDialog,
  setOpenMergeDialog,
  selectedCols,
  setSelectedCols,
  setCallTaxonomyMerge,
  callTaxonomyMerge,
  nonMergedColumns,
}) => {
  const [loader, setLoader] = useState(false);
  const [columnName, setColumnName] = useState('');
  const [taxonomyType, setTaxonomyType] = useState('');
  const [description, setDescription] = useState('');
  const [isMandatory, setIsMandatory] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    console.log('Non-Merged Columns:', nonMergedColumns);
    console.log('Selected Cols:', selectedCols);
  }, [nonMergedColumns, selectedCols]);

  const getSelectedBrandId = (): number | null => {
    if (!selectedCols.length) return null;

    const selectedColumn = nonMergedColumns.find((col) => col.id === selectedCols[0]);
    if (!selectedColumn) {
      console.error(`No column found for selected ID: ${selectedCols[0]}`);
      return null;
    }
    return selectedColumn.brand_id;
  };

  const validateInputs = (): boolean => {
    if (!selectedCols.length) {
      setValidationError('Please select at least one column to merge.');
      return false;
    }

    if (!columnName.trim()) {
      setValidationError('Column name is required.');
      return false;
    }

    if (!taxonomyType) {
      setValidationError('Taxonomy type is required.');
      return false;
    }

    const brandId = getSelectedBrandId();
    if (!brandId) {
      setValidationError('Unable to determine the brand ID for the selected columns.');
      return false;
    }

    setValidationError(null);
    return true;
  };

  const handleMergeConfirm = async () => {
    if (!validateInputs()) return;

    setLoader(true);
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const brandId = getSelectedBrandId();

    const payload: AddCDTypeRequest = {
      name: columnName.trim(),
      taxonomy_type: taxonomyType,
      description: description.trim() || undefined,
      created_by: user.id,
      merged_column_ids: selectedCols,
      brand_id: brandId!,
    };

    try {
      const response = await apiClient.post(`txnmy/api/v1/cdtype/add`, payload, {
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.data.message === 'success') {
        const sourceNames = selectedCols.map((id) => {
          const col = nonMergedColumns.find((c) => c.id === id);
          return col?.name || `ID ${id}`;
        });
        const brandName =
          nonMergedColumns.find((c) => c.id === selectedCols[0])?.brand_name ||
          "";
        const logDesc = {
          message: buildSummary(
            "Merged taxonomy type",
            columnName.trim(),
            "created"
          ),
          status: response.status,
          method: response.config?.method || "POST",
          url: response.config?.url || "/txnmy/api/v1/cdtype/add",
          category: "taxonomy",
          action: "create_merged_cdtype",
          details: [
            { label: "Name", value: columnName.trim() || "Not Set" },
            {
              label: "Taxonomy Type",
              value: taxonomyType ? String(taxonomyType) : "Not Set",
            },
            {
              label: "Brand ID",
              value: formatIdWithName(brandId, brandName),
            },
            {
              label: "Description",
              value: description.trim() || "Not Set",
            },
            {
              label: "Merged From",
              value: sourceNames.join(", ") || "None",
            },
          ],
          changes: [
            {
              label: "Merged Types",
              added: sourceNames,
              removed: [],
            },
          ],
        };
        await actionLog?.actionLogTrack(JSON.stringify(logDesc), false);

        Toastify('success', 'Merged column created successfully.');
        handleDialogClose(true);
        setCallTaxonomyMerge(!callTaxonomyMerge);
      } else {
        throw new Error('Unexpected response format.');
      }
    } catch (error: any) {
      console.error(error);
      const rawDetail = error.response?.data?.detail;
      const detailStr = typeof rawDetail === 'string' ? rawDetail : JSON.stringify(rawDetail ?? '');
      const isDuplicate = /duplicate|unique constraint|unq_custom_domain/i.test(detailStr);
      const errorMessage = isDuplicate
        ? 'This column already exists for this brand and taxonomy type.'
        : (typeof rawDetail === 'string' ? rawDetail : null) || 'Failed to create merge.';
      Toastify('error', errorMessage);

      const errorLogDesc = {
        message: `Failed to create merged taxonomy type "${columnName.trim() || "N/A"}": ${errorMessage}`,
        status: error.response?.status || 500,
        method: "POST",
        url: "/txnmy/api/v1/cdtype/add",
        category: "taxonomy",
        action: "create_merged_cdtype",
        error: errorMessage,
      };
      await actionLog?.actionLogTrack(JSON.stringify(errorLogDesc), true);
    } finally {
      setLoader(false);
    }
  };

  const handleDialogClose = (clearSelection = false) => {
    setOpenMergeDialog(false);
    if (clearSelection) {
      setSelectedCols([]);
    }
    setColumnName('');
    setTaxonomyType('');
    setDescription('');
    setIsMandatory(false);
    setValidationError(null);
  };

  return (
    <Sheet open={openMergeDialog} onOpenChange={(open) => !open && handleDialogClose()}>
      <SheetContent side="right" className="sm:max-w-md flex flex-col h-full" onInteractOutside={(e) => e.preventDefault()} onPointerDownOutside={(e) => e.preventDefault()}>
        <SheetHeader>
          <SheetTitle>Create New Merged Column</SheetTitle>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto min-h-0 py-4">
          <div className="space-y-4">
            {validationError && (
              <div className="bg-destructive/10 border border-destructive/20 text-destructive rounded-lg p-3 flex items-center gap-2">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span className="text-sm">{validationError}</span>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="column-name">Column Name *</Label>
              <Input
                id="column-name"
                placeholder="Enter column name"
                value={columnName}
                onChange={(e) => setColumnName(e.target.value)}
                className={!columnName.trim() && validationError ? 'border-destructive' : ''}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="taxonomy-type">Taxonomy Type *</Label>
              <SingleSelect
                options={taxonomyTypes.map((type) => ({
                  label: type.charAt(0).toUpperCase() + type.slice(1),
                  value: type,
                }))}
                defaultValue={taxonomyType}
                onChange={setTaxonomyType}
                placeholder="Select taxonomy type"
                containerClassName={`w-full ${!taxonomyType && validationError ? 'border-destructive' : ''}`}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Enter description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="resize-none"
              />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="is-mandatory"
                checked={isMandatory}
                onCheckedChange={(checked) => setIsMandatory(checked === true)}
              />
              <Label htmlFor="is-mandatory" className="font-normal cursor-pointer">
                Is Mandatory
              </Label>
            </div>
          </div>
        </div>
        <SheetFooter className="gap-2 border-t pt-4 mt-auto flex-shrink-0">
          <Button variant="outline" onClick={() => handleDialogClose()} disabled={loader}>
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleMergeConfirm}
            disabled={
              loader || !columnName.trim() || !taxonomyType || !getSelectedBrandId()
            }
            className="bg-primary hover:bg-primary/90"
          >
            {loader ? <SpinnerLoader className="h-4 w-4 mr-2" /> : null}
            {loader ? 'Creating...' : 'Create Merged Column'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default TaxonomyMergedCreateDialog;
